<script setup lang="ts">
/**
 * The brand's environment — neon wash, CRT scanlines, film grain — as an absolutely-positioned
 * layer a container opts into. Extracted from apps/www's BackgroundStage so the marketing site and
 * the product draw the same room.
 *
 * Where this may be used is a deliberate boundary. It belongs on *chrome*: the sign-in splash, the
 * sidebar, the header, empty states, modal scrims. It must never sit behind tables, execution
 * graphs or forms — a 3px scanline pitch over 11px mono table rows moirés and costs the contrast
 * that BG §22 ("dense but clear", "trustworthy") depends on. Atmosphere where you look, calm where
 * you work.
 *
 * Decorative throughout, so the whole layer is aria-hidden and never focusable.
 */
withDefaults(
    defineProps<{
        /**
         * `full` is the sign-in splash — the one screen with no data on it. `chrome` is the quieter
         * mix for sidebar and header, where text sits directly on top all day.
         */
        intensity?: 'full' | 'chrome'
    }>(),
    { intensity: 'chrome' },
)
</script>

<template>
    <div class="zh-backdrop" :data-intensity="intensity" aria-hidden="true">
        <div class="zh-backdrop__neon" />
        <div class="zh-backdrop__shade" />
        <div class="zh-backdrop__scanlines" />
        <div class="zh-backdrop__grain" />
    </div>
</template>

<style scoped>
.zh-backdrop,
.zh-backdrop > * {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.zh-backdrop {
    z-index: 0;
    overflow: hidden;
    background: var(--void-black);
}

/* BG §17.1's two-sided neon: magenta left, cyan right, meeting in darkness in the middle. */
.zh-backdrop__neon {
    background:
        radial-gradient(
            60rem 45rem at -10% 35%,
            color-mix(in srgb, var(--neon-magenta) 30%, transparent) 0%,
            transparent 62%
        ),
        radial-gradient(
            55rem 42rem at 110% 60%,
            color-mix(in srgb, var(--electric-cyan) 24%, transparent) 0%,
            transparent 62%
        );
    mix-blend-mode: screen;
    opacity: 0.85;
}

.zh-backdrop__shade {
    background:
        radial-gradient(
            120% 90% at 50% 45%,
            transparent 18%,
            color-mix(in srgb, var(--void-black) 85%, transparent) 100%
        ),
        color-mix(in srgb, var(--void-black) 58%, transparent);
}

/* 3px pitch: visible as texture, never as a moiré on text. */
.zh-backdrop__scanlines {
    background: repeating-linear-gradient(
        to bottom,
        color-mix(in srgb, var(--void-black) 28%, transparent) 0 1px,
        transparent 1px 3px
    );
    opacity: 0.55;
}

/* Inline SVG turbulence rather than a PNG: no extra request and no binary in the repo. */
.zh-backdrop__grain {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.42'/%3E%3C/svg%3E");
    opacity: 0.16;
    mix-blend-mode: overlay;
}

/*
 * Chrome carries UI text on top of it permanently, so every layer drops to roughly a third. The
 * room is still there; it stops competing with the words.
 */
.zh-backdrop[data-intensity='chrome'] .zh-backdrop__neon {
    opacity: 0.3;
}

.zh-backdrop[data-intensity='chrome'] .zh-backdrop__shade {
    background: color-mix(in srgb, var(--void-black) 35%, transparent);
}

.zh-backdrop[data-intensity='chrome'] .zh-backdrop__scanlines {
    opacity: 0.18;
}

.zh-backdrop[data-intensity='chrome'] .zh-backdrop__grain {
    opacity: 0.07;
}

/* Less noise on small screens, where text has nowhere else to go. */
@media (max-width: 47.99rem) {
    .zh-backdrop__scanlines {
        opacity: 0.35;
    }

    .zh-backdrop__grain {
        opacity: 0.1;
    }
}
</style>
